import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info, Megaphone, Plus } from 'lucide-react';

import { request } from '@/api/client';
import type { CommunityPost, Page, PostStatus } from '@/api/types';
import { PageHeader } from '@/components/AppShell';
import { DataTable, PageSizeSelect, Pagination, type Column } from '@/components/DataTable';
import { usePageSize } from '@/lib/pageSize';
import { useToast } from '@/components/Toast';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Field,
  Modal,
  Select,
  TableSkeleton,
  TextArea,
  TextInput,
} from '@/components/ui';
import { dateTime } from '@/lib/format';
import { useResource } from '@/lib/hooks';

export function CommunityPage() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const [status, setStatus] = useState<PostStatus | ''>('');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<CommunityPost | 'new' | null>(null);
  const [deleting, setDeleting] = useState<CommunityPost | null>(null);
  const language = i18n.language;
  const [pageSize, setPageSize] = usePageSize('community');

  const { data, error, loading, reload } = useResource<Page<CommunityPost>>(
    '/api/v1/admin/community/posts',
    { status: status || undefined, page, page_size: pageSize },
    [status, page, pageSize],
  );

  async function remove(post: CommunityPost) {
    try {
      await request(`/api/v1/admin/community/posts/${post.id}`, { method: 'DELETE' });
      toast.success(t('community.deleted'));
      setDeleting(null);
      reload();
    } catch {
      toast.failure(t('common.loadFailed'));
    }
  }

  const columns: Column<CommunityPost>[] = [
    {
      key: 'title',
      header: t('community.titleDe'),
      render: (post) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-ink">{post.title_de}</div>
          <div className="truncate text-2xs text-ink-subtle">{post.title_en}</div>
        </div>
      ),
    },
    {
      key: 'topic',
      header: t('community.topic'),
      render: (post) => <Badge tone="neutral">{post.topic}</Badge>,
    },
    {
      key: 'status',
      header: t('community.status'),
      render: (post) => (
        <Badge tone={post.status === 'published' ? 'positive' : 'neutral'}>
          {t(`community.${post.status}`)}
        </Badge>
      ),
    },
    {
      key: 'publishedAt',
      header: t('community.publishedAt'),
      align: 'right',
      render: (post) => (
        <span className="text-ink-subtle">
          {dateTime(post.published_at, language) ?? <span aria-hidden>—</span>}
        </span>
      ),
    },
    {
      key: 'actions',
      header: t('common.edit'),
      align: 'right',
      render: (post) => (
        <div className="flex justify-end gap-1">
          <Button className="text-xs" onClick={() => setEditing(post)}>
            {t('common.edit')}
          </Button>
          <Button
            className="text-xs"
            onClick={() => setDeleting(post)}
          >
            {t('common.delete')}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('community.title')}
        subtitle={t('community.subtitle')}
        actions={
          <Button
            variant="primary"
            icon={<Plus className="h-4 w-4" />}
            onClick={() => setEditing('new')}
          >
            {t('community.create')}
          </Button>
        }
      />

      <div className="card">
        <div className="flex items-center gap-3 border-b border-line p-3">
          <Select
            aria-label={t('community.status')}
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as PostStatus | '');
              setPage(1);
            }}
          >
            <option value="">{t('common.all')}</option>
            <option value="published">{t('community.published')}</option>
            <option value="draft">{t('community.draft')}</option>
          </Select>
          <PageSizeSelect
            value={pageSize}
            onChange={(next) => {
              setPageSize(next);
              setPage(1);
            }}
          />
        </div>

        {error ? (
          <ErrorState message={t('common.loadFailed')} onRetry={reload} />
        ) : loading && !data ? (
          <TableSkeleton columns={5} />
        ) : data && data.items.length === 0 ? (
          <EmptyState
            icon={<Megaphone className="h-8 w-8" />}
            title={t('community.emptyTitle')}
            body={t('community.emptyBody')}
            action={
              <Button variant="primary" onClick={() => setEditing('new')}>
                {t('community.create')}
              </Button>
            }
          />
        ) : (
          <>
            <DataTable
              caption={t('community.title')}
              columns={columns}
              rows={data?.items ?? []}
              rowKey={(post) => post.id}
            />
            <Pagination
              page={page}
              pageSize={pageSize}
              total={data?.total ?? 0}
              onPage={setPage}
            />
          </>
        )}
      </div>

      <p className="mt-3 flex items-start gap-1.5 text-xs text-ink-subtle">
        <Info aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        {t('community.editDoesNotBump')}
      </p>

      {editing ? (
        <EditPost
          post={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(created) => {
            setEditing(null);
            toast.success(t(created ? 'community.created' : 'community.updated'));
            reload();
          }}
        />
      ) : null}

      {deleting ? (
        <Modal
          open
          onClose={() => setDeleting(null)}
          title={t('community.deleteTitle')}
          footer={
            <>
              <Button onClick={() => setDeleting(null)}>{t('common.cancel')}</Button>
              <Button variant="danger" onClick={() => remove(deleting)}>
                {t('common.delete')}
              </Button>
            </>
          }
        >
          <p className="text-sm text-ink-muted">
            {t('community.deleteBody', { title: deleting.title_de })}
          </p>
        </Modal>
      ) : null}
    </>
  );
}

function EditPost({
  post,
  onClose,
  onSaved,
}: {
  post: CommunityPost | null;
  onClose: () => void;
  onSaved: (created: boolean) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [topic, setTopic] = useState(post?.topic ?? '');
  const [titleDe, setTitleDe] = useState(post?.title_de ?? '');
  const [titleEn, setTitleEn] = useState(post?.title_en ?? '');
  const [bodyDe, setBodyDe] = useState(post?.body_de ?? '');
  const [bodyEn, setBodyEn] = useState(post?.body_en ?? '');
  const [busy, setBusy] = useState(false);

  const complete =
    topic.trim() && titleDe.trim() && titleEn.trim() && bodyDe.trim() && bodyEn.trim();

  async function save(status: PostStatus) {
    setBusy(true);
    const body = {
      topic: topic.trim(),
      title_de: titleDe.trim(),
      title_en: titleEn.trim(),
      body_de: bodyDe.trim(),
      body_en: bodyEn.trim(),
      status,
    };
    try {
      if (post) {
        await request(`/api/v1/admin/community/posts/${post.id}`, {
          method: 'PATCH',
          body,
        });
      } else {
        await request('/api/v1/admin/community/posts', { method: 'POST', body });
      }
      onSaved(!post);
    } catch {
      toast.failure(t('common.loadFailed'));
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t(post ? 'community.editTitle' : 'community.createTitle')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button loading={busy} disabled={!complete} onClick={() => save('draft')}>
            {t('community.saveDraft')}
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={!complete}
            onClick={() => save('published')}
          >
            {t('community.publish')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="rounded-lg bg-surface-sunken px-3 py-2 text-xs text-ink-muted">
          {t('community.bothLanguages')}
        </p>

        <Field label={t('community.topic')}>
          {(id) => (
            <TextInput
              id={id}
              maxLength={60}
              placeholder={t('community.topicPlaceholder')}
              value={topic}
              onChange={(event) => setTopic(event.target.value)}
            />
          )}
        </Field>

        <Field label={t('community.titleDe')}>
          {(id) => (
            <TextInput
              id={id}
              lang="de"
              maxLength={200}
              value={titleDe}
              onChange={(event) => setTitleDe(event.target.value)}
            />
          )}
        </Field>
        <Field label={t('community.bodyDe')}>
          {(id) => (
            <TextArea
              id={id}
              lang="de"
              value={bodyDe}
              onChange={(event) => setBodyDe(event.target.value)}
            />
          )}
        </Field>

        <Field label={t('community.titleEn')}>
          {(id) => (
            <TextInput
              id={id}
              lang="en"
              maxLength={200}
              value={titleEn}
              onChange={(event) => setTitleEn(event.target.value)}
            />
          )}
        </Field>
        <Field label={t('community.bodyEn')}>
          {(id) => (
            <TextArea
              id={id}
              lang="en"
              value={bodyEn}
              onChange={(event) => setBodyEn(event.target.value)}
            />
          )}
        </Field>
      </div>
    </Modal>
  );
}
